import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RecaptchaEnterprise from '../../../components/ui/RecaptchaEnterprise';
import recaptchaEnterpriseService from '../../../utils/recaptchaEnterpriseService';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import RegistrationSuccessDialog from '../../../components/auth/RegistrationSuccessDialog';
import authService from '../../../utils/authService';

const RegisterForm = ({ selectedRole, isLoading, setIsLoading }) => {
  const [searchParams] = useSearchParams();
  
  // Get email from URL if present (for invitations)
  const urlEmail = searchParams.get('email');
  let decodedEmail = '';
  if (urlEmail) {
    try {
      decodedEmail = atob(urlEmail);
    } catch {
      decodedEmail = '';
    }
  }
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: decodedEmail || '', // Pre-fill email if from invitation
    password: '',
    confirmPassword: '',
    role: selectedRole,
    agreeToTerms: false,
    skills: '',
    experienceLevel: '',
    hourlyRate: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaScore, setRecaptchaScore] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(null);
  const [recaptchaVerifying, setRecaptchaVerifying] = useState(false);
  const [emailCheckInProgress, setEmailCheckInProgress] = useState(false);
  const navigate = useNavigate();
  const { authError, clearError } = useAuth();
  const recaptchaRef = useRef(null);
  const emailCheckTimeoutRef = useRef(null);

  // Test connection on component mount
  useEffect(() => {
    const testConnection = async () => {
      const result = await authService.testConnection();
      setConnectionStatus(result);
      
      if (!result.connected) {
        setErrors({
          connection: result.error
        });
      }
    };
    
    testConnection();
  }, []);

  // Update role when selectedRole prop changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, role: selectedRole }));
  }, [selectedRole]);

  // Load reCAPTCHA Enterprise script
  useEffect(() => {
    let mounted = true;
    
    const loadRecaptcha = async () => {
      const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        console.error('reCAPTCHA site key not configured');
        if (mounted) {
          setRecaptchaError('reCAPTCHA not configured. Please contact support.');
          setErrors(prev => ({ 
            ...prev, 
            recaptcha: 'reCAPTCHA not configured. Please contact support.' 
          }));
        }
        return;
      }

      try {
        await recaptchaEnterpriseService.loadScript(siteKey);
        if (mounted) {
          setRecaptchaLoaded(true);
          setRecaptchaError(null);
          // Clear any previous reCAPTCHA errors
          setErrors(prev => {
            const { recaptcha, ...rest } = prev;
            return rest;
          });
        }
      } catch (error) {
        console.error('Failed to load reCAPTCHA:', error);
        if (mounted) {
          const errorMessage = 'Failed to load reCAPTCHA. Please refresh the page.';
          setRecaptchaError(errorMessage);
          setRecaptchaLoaded(false);
          setErrors(prev => ({ 
            ...prev, 
            recaptcha: errorMessage
          }));
        }
      }
    };

    loadRecaptcha();
    
    // Cleanup function
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Cleanup email check timeout on unmount
  useEffect(() => {
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
  }, []);

  // Monitor modal state changes in development environment only
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Only log if verification modal state changes to avoid console noise
      if (showVerificationModal) {
        console.log('Verification modal displayed:', {
          email: registeredEmail,
          timestamp: new Date().toISOString()
        });
      }
    }
  }, [showVerificationModal, registeredEmail]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (authError) {
      clearError();
    }
    // Clear connection error when user interacts
    if (errors.connection) {
      setErrors(prev => ({ ...prev, connection: '' }));
    }
  };

  // Handle email blur to check for duplicates in real-time (with debounce)
  const handleEmailBlur = async () => {
    const email = formData.email?.trim();
    
    // Clear any existing timeout
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }
    
    // Only check if email is valid format and not invited
    if (!email || !/\S+@\S+\.\S+/.test(email) || decodedEmail) {
      return;
    }
    
    // Debounce the check to avoid excessive API calls
    setEmailCheckInProgress(true);
    emailCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const emailCheckResult = await authService.checkEmailExists(email);
        if (emailCheckResult.exists) {
          setErrors(prev => ({
            ...prev,
            email: 'This email is already registered. Please sign in instead or use a different email.'
          }));
        } else {
          // Clear email error if email doesn't exist
          setErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors.email?.includes('already registered')) {
              delete newErrors.email;
            }
            return newErrors;
          });
        }
      } catch (error) {
        console.error('Error checking email existence on blur:', error);
        // Don't show error on check failure - let user proceed
      } finally {
        setEmailCheckInProgress(false);
      }
    }, 500); // 500ms debounce
  };

  const validateForm = async () => {
    const newErrors = {};
    
    if (!formData.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    } else {
      // Check if email already exists - this is now a required validation step
      try {
        const emailCheckResult = await authService.checkEmailExists(formData.email.trim());
        if (emailCheckResult.exists) {
          newErrors.email = 'This email is already registered. Please sign in instead or use a different email.';
        }
      } catch (error) {
        console.error('Error checking email existence:', error);
        // Don't block registration on check failure - let backend handle it
        // But log for debugging
      }
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must include uppercase, lowercase, and a number';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    // reCAPTCHA validation - only show error if there's an actual error, not for missing token
    // Token will be generated automatically during form submission
    if (recaptchaError) {
      newErrors.recaptcha = recaptchaError;
    } else if (!recaptchaLoaded) {
      newErrors.recaptcha = 'Security verification is loading. Please wait...';
    }
    
    // Only validate VA-specific fields for VA role (not for admin/moderator)
    if (formData.role === 'va') {
      if (!formData.skills?.trim()) {
        newErrors.skills = 'Key skills are required';
      }
      if (!formData.experienceLevel) {
        newErrors.experienceLevel = 'Experience level is required';
      }
      if (!formData.hourlyRate || formData.hourlyRate < 2 || formData.hourlyRate > 100) {
        newErrors.hourlyRate = 'Hourly rate must be between $2 and $100 USD';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getErrorMessage = (result) => {
    if (result?.type) {
      switch (result.type) {
        case 'network_error':
          return 'Cannot connect to the server. Please check your internet connection and try again.';
        case 'user_exists':
          return 'An account with this email already exists. Please sign in instead or use a different email.';
        case 'weak_password':
          return 'Password must be at least 6 characters long and include uppercase, lowercase, and a number.';
        case 'signup_disabled':
          return 'Account registration is currently disabled. Please contact support.';
        case 'invalid_email':
          return 'Please enter a valid email address.';
        case 'schema_error':
          return 'Database configuration issue. Please contact support.';
        default:
          return result.error || 'Registration failed. Please try again.';
      }
    }
    return result?.error || authError || 'Registration failed. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // validateForm is now async due to email existence check
    const isValid = await validateForm();
    if (!isValid) return;
    
    // Check connection before attempting signup
    if (connectionStatus && !connectionStatus.connected) {
      setErrors({ 
        submit: 'Cannot connect to the authentication service. Please check your internet connection and try again.' 
      });
      return;
    }
    
    setIsLoading(true);
    clearError();
    setErrors({});
    
    try {
      // Execute and verify reCAPTCHA Enterprise if loaded
      let recaptchaVerification = null;
      
      // Check if reCAPTCHA is loaded and ready
      if (!recaptchaLoaded) {
        setErrors({
          recaptcha: 'Security verification is still loading. Please wait a moment and try again.',
          submit: 'Please wait for security verification to complete.'
        });
        setIsLoading(false);
        return;
      }
      
      if (recaptchaError) {
        setErrors({
          recaptcha: recaptchaError,
          submit: 'Security verification error. Please refresh the page.'
        });
        setIsLoading(false);
        return;
      }
      
      const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
      
      try {
        console.log('🔒 Executing reCAPTCHA Enterprise...');
        setRecaptchaVerifying(true);

        // Always generate a fresh token
        recaptchaVerification = await recaptchaEnterpriseService.executeAndVerify(siteKey, 'register');
        
        if (!recaptchaVerification.success) {
          setRecaptchaVerifying(false);
          setRecaptchaError(recaptchaVerification.error);
          setErrors({
            recaptcha: recaptchaVerification.error || 'reCAPTCHA verification failed',
            submit: 'Security verification failed. Please try again.'
          });
          setIsLoading(false);
          return;
        }
        
        console.log('✅ reCAPTCHA verification successful. Score:', recaptchaVerification.score);
        setRecaptchaVerifying(false);
        setRecaptchaScore(recaptchaVerification.score);
        // Don't store the token in state to prevent reuse
        // setRecaptchaToken(recaptchaVerification.token);
        setRecaptchaError(null);
      } catch (error) {
        console.error('reCAPTCHA execution error:', error);
        setRecaptchaVerifying(false);
        const errorMessage = 'Security verification failed. Please refresh the page and try again.';
        setRecaptchaError(errorMessage);
        setErrors({
          recaptcha: errorMessage,
          submit: errorMessage
        });
        setIsLoading(false);
        return;
      }

      const userData = {
        fullName: formData.fullName.trim(),
        role: formData.role,
        skills: formData.skills.trim(),
        experienceLevel: formData.experienceLevel,
        hourlyRate: formData.hourlyRate,
        recaptchaToken: recaptchaVerification.token,
        recaptchaScore: recaptchaVerification.score
      };
      
      // Use authService directly instead of AuthContext's signUp
      // This avoids the auto-login behavior
      const result = await authService.signUp(formData.email.trim(), formData.password, userData);
      
      if (result?.success) {
        // Ensure user is signed out after registration to prevent auto-login
        try {
          await authService.signOut();
        } catch (signOutError) {
          // Continue even if sign out fails
          if (process.env.NODE_ENV === 'development') {
            console.warn('Sign out after registration failed:', signOutError);
          }
        }
        
        // Check if email was sent successfully
        const emailSent = result.verificationEmailSent && !result.warnings?.some(w => w.type === 'email_not_sent' || w.type === 'email_error');
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('📧 Registration result:', {
            verificationEmailSent: result.verificationEmailSent,
            hasWarnings: !!result.warnings,
            warnings: result.warnings,
            emailSent: emailSent
          });
        }
        
        // First set the email for the modal
        setRegisteredEmail(formData.email.trim());
        
        // Clear any existing errors/messages before showing modal
        setErrors({});
        
        // Always stop loading first
        setIsLoading(false);
        
        // Reset reCAPTCHA
        setRecaptchaToken(null);
        setRecaptchaScore(null);
        setRecaptchaError(null);
        
        // Show appropriate message based on email status
        if (!emailSent) {
          const warningMessage = result.warnings?.[0]?.message || 
                                'Registration successful, but verification email could not be sent. Please contact support to verify your account.';
          
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Email not sent during registration');
          }
          
          setErrors({
            submit: warningMessage
          });
        } else {
          // Email sent successfully, show modal
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Showing verification modal for:', formData.email.trim());
          }
          
          setShowVerificationModal(true);
        }
      } else if (result?.type === 'user_exists') {
        // Special handling for existing users
        setErrors({ 
          submit: 'This email is already registered. You can sign in instead.',
          email: 'Email already registered'
        });
        
        // Add a small delay and then switch to login tab with this email
        setTimeout(() => {
          navigate('/login-register?tab=login', { 
            replace: true,
            state: { 
              message: 'This email is already registered. Please sign in with your existing account.',
              email: formData.email.trim()
            }
          });
        }, 3000);
      } else {
        setErrors({ 
          submit: getErrorMessage(result)
        });
        // Reset reCAPTCHA on error
        setRecaptchaToken(null);
        setRecaptchaScore(null);
        setRecaptchaError(null);
      }
    } catch (error) {
      // Log error in development mode only
      if (process.env.NODE_ENV === 'development') {
        console.error('Registration error:', error);
      }
      setErrors({ 
        submit: 'An unexpected error occurred during registration. Please try again.' 
      });
      // Reset reCAPTCHA on error
      setRecaptchaToken(null);
      setRecaptchaScore(null);
      setRecaptchaError(null);
    } finally {
      // Only set loading to false if we didn't have a successful registration
      // For successful registration, we already set it to false before showing the modal
      if (!registeredEmail) {
        setIsLoading(false);
      }
    }
  };

  const handleRetryConnection = async () => {
    setErrors({});
    const result = await authService.testConnection();
    setConnectionStatus(result);
    
    if (!result.connected) {
      setErrors({
        connection: result.error
      });
    } else {
      setErrors({
        success: 'Connection restored successfully!'
      });
    }
  };

  const displayError = errors.submit || errors.connection || authError;
  const displaySuccess = !showVerificationModal && errors.success;
  const displayWarning = errors.warning;


  return (
    <div className="space-y-6">
      {/* Registration Success Dialog */}
      <RegistrationSuccessDialog
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        email={registeredEmail}
        onGoToLogin={() => {
          setShowVerificationModal(false);
          navigate('/login-register?tab=login', {
            replace: true,
            state: {
              message: 'Registration successful! Please check your email and then sign in.',
              email: registeredEmail
            }
          });
        }}
      />
      {/* Connection Status */}
      {connectionStatus && !connectionStatus.connected && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <Icon name="AlertTriangle" size={20} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 mb-1">Connection Issue</h4>
              <p className="text-sm text-red-700 mb-3">{connectionStatus.error}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRetryConnection}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Icon name="RefreshCw" size={16} className="mr-1" />
                Retry Connection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {displaySuccess && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700 flex items-start">
            <Icon name="CheckCircle" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <span>{displaySuccess}</span>
          </p>
        </div>
      )}
      
      {/* Warning Message */}
      {displayWarning && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700 flex items-start">
            <Icon name="AlertTriangle" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <span>{displayWarning}</span>
          </p>
        </div>
      )}
      
      {/* Error Message */}
      {displayError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700 flex items-start">
            <Icon name="AlertCircle" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <span>{displayError}</span>
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Full Name Field */}
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-text-primary mb-2">
            Full Name
          </label>
          <Input
            id="fullName"
            name="fullName"
            type="text"
            placeholder="Enter your full name"
            value={formData.fullName}
            onChange={handleInputChange}
            disabled={isLoading}
            required
            className={errors.fullName ? 'border-error' : ''}
            autoComplete="name"
          />
          {errors.fullName && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.fullName}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
            Email Address {decodedEmail && <span className="text-sm text-text-secondary">(from invitation)</span>}
          </label>
          <div className="relative">
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleEmailBlur}
              disabled={isLoading || !!decodedEmail} // Disable if email comes from invitation
              required
              className={errors.email ? 'border-error' : ''}
              autoComplete="email"
            />
            {emailCheckInProgress && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin">
                  <Icon name="Loader" size={18} className="text-text-secondary" />
                </div>
              </div>
            )}
          </div>
          {decodedEmail && (
            <p className="mt-1 text-sm text-text-secondary">
              This email address was provided through your invitation and cannot be changed.
            </p>
          )}
          {errors.email && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.email}
            </p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
            Password
          </label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading}
              required
              className={`pr-12 ${errors.password ? 'border-error' : ''}`}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary"
              disabled={isLoading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.password}
            </p>
          )}
          <p className="mt-1 text-xs text-text-secondary">
            Must include uppercase, lowercase, and a number. Minimum 6 characters.
          </p>
        </div>

        {/* Confirm Password Field */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              disabled={isLoading}
              required
              className={`pr-12 ${errors.confirmPassword ? 'border-error' : ''}`}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary"
              disabled={isLoading}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.confirmPassword}
            </p>
          )}
        </div>

        {/* Role Display - Only show for VA and Employer */}
        {(formData.role === 'va' || formData.role === 'employer') && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Account Type
            </label>
            <div className="p-3 bg-accent-50 border border-accent-200 rounded-lg">
              <p className="text-sm text-accent-700 font-medium">
                {formData.role === 'va' ? 'Virtual Assistant' : 'Employer'}
              </p>
              <p className="text-xs text-accent-600 mt-1">
                {formData.role === 'va'
                  ? 'You will be able to search and apply for jobs'
                  : 'You will be able to post jobs and hire virtual assistants'}
              </p>
            </div>
          </div>
        )}

        {formData.role === 'va' && (
          <>
            {/* Skills Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Key Skills
              </label>
              <Input
                type="text"
                name="skills"
                value={formData.skills}
                onChange={handleInputChange}
                placeholder="e.g. Excel, Social Media, Customer Support"
                className="w-full"
              />
              {errors.skills && (
                <p className="mt-1 text-sm text-error flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  {errors.skills}
                </p>
              )}
            </div>

            {/* Experience Level */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Experience Level
              </label>
              <select
                name="experienceLevel"
                value={formData.experienceLevel}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
              >
                <option value="">Select experience level</option>
                <option value="entry">Entry Level (0-2 years)</option>
                <option value="mid">Mid Level (2-5 years)</option>
                <option value="senior">Senior Level (5+ years)</option>
              </select>
              {errors.experienceLevel && (
                <p className="mt-1 text-sm text-error flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  {errors.experienceLevel}
                </p>
              )}
            </div>

            {/* Hourly Rate */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Desired Hourly Rate (USD)
              </label>
              <Input
                type="number"
                name="hourlyRate"
                value={formData.hourlyRate}
                onChange={handleInputChange}
                min="2"
                max="100"
                className="w-full"
              />
              {errors.hourlyRate && (
                <p className="mt-1 text-sm text-error flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  {errors.hourlyRate}
                </p>
              )}
            </div>
          </>
        )}

        {/* reCAPTCHA Enterprise */}
        <div>
          <div className="mb-2">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Security Verification
            </label>
            {recaptchaError ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={16} className="text-red-600 mr-2" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 font-medium">
                      Security Verification Error
                    </p>
                    <p className="text-xs text-red-700">
                      {recaptchaError}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.reload()}
                    className="border-red-300 text-red-700 hover:bg-red-50 ml-2"
                  >
                    <Icon name="RefreshCw" size={14} className="mr-1" />
                    Refresh
                  </Button>
                </div>
              </div>
            ) : recaptchaVerifying ? (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-sm text-blue-800 font-medium">Verifying security...</span>
                </div>
              </div>
            ) : !recaptchaLoaded ? (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                  <span className="text-sm text-text-secondary">Loading security verification...</span>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <Icon name="Shield" size={16} className="text-green-600 mr-2" />
                  <div className="flex-1">
                    <p className="text-sm text-green-800 font-medium">
                      reCAPTCHA Enterprise Protection Active
                    </p>
                    <p className="text-xs text-green-700">
                      Your submission will be automatically verified for security.
                      {recaptchaScore && (
                        <span className="ml-1">Security Score: {(recaptchaScore * 100).toFixed(0)}%</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {errors.recaptcha && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.recaptcha}
            </p>
          )}
        </div>

        {/* Terms and Conditions */}
        <div>
          <label className="flex items-start">
            <Input
              type="checkbox"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleInputChange}
              disabled={isLoading}
              className="w-4 h-4 mr-3 mt-0.5 flex-shrink-0"
            />
            <span className="text-sm text-text-secondary">
              I agree to the{' '}
              <a href="/terms-of-service" className="text-primary hover:text-primary-600 underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy-policy" className="text-primary hover:text-primary-600 underline">
                Privacy Policy
              </a>
            </span>
          </label>
          {errors.agreeToTerms && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.agreeToTerms}
            </p>
          )}
        </div>

        {/* Submit Error */}
        {displayError && (
          <div className="p-3 bg-error-50 border border-error-200 rounded-lg">
            <p className="text-sm text-error flex items-start">
              <Icon name="AlertCircle" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
              <span>{displayError}</span>
            </p>
          </div>
        )}

        {/* Submit Button */}
        <div>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={isLoading}
            isLoading={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </div>

        {/* Email Notification Info */}
        <div className="mt-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
          <p className="text-xs text-accent-700 font-medium mb-1">Email Notifications</p>
          <p className="text-xs text-accent-600">
            <Icon name="Mail" size={12} className="inline mr-1" />
            You will receive email notifications for account activities including registration confirmation and login alerts.
          </p>
        </div>

        {/* Connection Status Footer */}
        {connectionStatus?.connected && (
          <div className="text-center text-xs text-green-600 flex items-center justify-center">
            <Icon name="CheckCircle" size={12} className="mr-1" />
            Connected to Database
          </div>
        )}
      </form>
    </div>
  );
};

export default RegisterForm;

